import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Home, 
  Building2, 
  PaintBucket, 
  Hammer,
  Bath,
  UtensilsCrossed,
  TreeDeciduous,
  HardHat,
  CheckCircle2
} from 'lucide-react';
import { Button } from '../components/ui/button';

const IMAGES = {
  residential: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/k2ta1fbc_Project%20A%20-%20After1.jpg",
  commercial: "https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/f3l6949n_Screenshot%202026-06-15%20at%203.54.10%E2%80%AFPMsmall.png",
  interior: "https://customer-assets.emergentagent.com/job_048130c6-d634-4556-a2f8-17438a24507e/artifacts/9gwndztb_Best%20Photo%202.png",
  bathroom: "https://customer-assets.emergentagent.com/job_048130c6-d634-4556-a2f8-17438a24507e/artifacts/d5kn60ek_Best%20Photo%204.png",
};

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const multifamilyServices = [
  {
    icon: Home,
    title: 'New Construction',
    description: 'Build multifamily properties from the ground up with custom designs.',
  },
  {
    icon: PaintBucket,
    title: 'Renovations',
    description: 'Transform your existing space with comprehensive renovations.',
  },
  {
    icon: Bath,
    title: 'Bathroom Remodeling',
    description: 'Modern bathroom upgrades and complete renovations.',
  },
  {
    icon: UtensilsCrossed,
    title: 'Kitchen Remodeling',
    description: 'Create functional kitchens with expert craftsmanship.',
  },
  {
    icon: TreeDeciduous,
    title: 'Outdoor Living',
    description: 'Decks, patios, and outdoor entertainment spaces.',
  },
  {
    icon: Hammer,
    title: 'Additions',
    description: 'Expand your living space with seamless home additions.',
  },
];

const commercialServices = [
  {
    icon: Building2,
    title: 'Office Buildings',
    description: 'Professional office spaces designed for productivity.',
  },
  {
    icon: HardHat,
    title: 'Retail Spaces',
    description: 'Storefronts and retail environments that attract customers.',
  },
  {
    icon: PaintBucket,
    title: 'Tenant Improvements',
    description: 'Customize commercial spaces for specific tenant needs.',
  },
  {
    icon: Hammer,
    title: 'Industrial Facilities',
    description: 'Warehouses and industrial buildings built to code.',
  },
];

const features = [
  'Licensed & Insured',
  'Free Estimates',
  'Quality Materials',
  'Expert Craftsmen',
  'On-Time Delivery',
  'Budget-Friendly',
];

export const Services = () => {
  return (
    <div className="min-h-screen pt-48" data-testid="services-page">
      {/* Hero Section */}
      <section className="py-20 bg-ktm-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <h1 className="font-chivo font-black text-4xl md:text-5xl text-ktm-navy leading-tight mb-6">
              Complete Construction<br />
              <span className="text-ktm-red">Solutions</span>
            </h1>
            <p className="text-slate-600 text-lg leading-relaxed">
              From multifamily properties to commercial buildings, KTM Construction 
              provides comprehensive construction services tailored to your needs. 
              Our experienced team delivers quality results on every project through knowledge and communication.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Multifamily Services */}
      <section className="py-24 bg-white" data-testid="multifamily-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
            >
              <div className="aspect-[4/3] overflow-hidden border-4 border-ktm-navy">
                <img 
                  src={IMAGES.residential} 
                  alt="Multifamily Construction" 
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div 
              className="space-y-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-ktm-red font-bold uppercase tracking-wider text-sm">
                Multifamily
              </span>
              <h2 className="font-chivo font-black text-3xl md:text-4xl text-ktm-navy">
                Multifamily Construction<br />& Remodeling
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed">
                Whether you&apos;re building a new multifamily property or renovating existing units, 
                our construction services cover every aspect of your project. 
                We work closely with property owners to bring their vision to life.
              </p>
              <ul className="grid grid-cols-2 gap-3">
                {features.slice(0, 4).map((feature) => (
                  <li key={feature} className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-ktm-red flex-shrink-0" />
                    <span className="text-slate-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {multifamilyServices.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-ktm-muted p-6 group hover:bg-ktm-navy transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`multifamily-service-${service.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="w-12 h-12 bg-ktm-navy group-hover:bg-ktm-red flex items-center justify-center mb-4 transition-colors">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-chivo font-bold text-lg text-ktm-navy group-hover:text-white mb-2 transition-colors">
                  {service.title}
                </h3>
                <p className="text-slate-600 group-hover:text-slate-300 text-sm leading-relaxed transition-colors">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Commercial Services */}
      <section className="py-24 bg-ktm-navy" data-testid="commercial-services">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl mx-auto text-center space-y-6 mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-ktm-red font-bold uppercase tracking-wider text-sm block">
              Commercial
            </span>
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-white">
              Commercial Construction
            </h2>
            <p className="text-slate-300 text-lg leading-relaxed">
              Our commercial construction services are designed to meet the unique 
              demands of business projects. From office buildings to retail spaces, 
              we deliver professional results that help your business succeed.
            </p>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl mx-auto text-left pt-2">
              {features.map((feature) => (
                <li key={feature} className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-ktm-red flex-shrink-0" />
                  <span className="text-slate-300">{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {commercialServices.map((service, index) => (
              <motion.div
                key={service.title}
                className="bg-white/10 backdrop-blur p-6 border border-white/20 group hover:bg-ktm-red transition-colors"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                data-testid={`commercial-service-${service.title.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="w-12 h-12 bg-ktm-red group-hover:bg-white flex items-center justify-center mb-4 transition-colors">
                  <service.icon className="w-6 h-6 text-white group-hover:text-ktm-navy" />
                </div>
                <h3 className="font-chivo font-bold text-lg text-white mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Interior Gallery — Quality Finishes (Showcase) */}
      <section className="relative py-28 bg-ktm-navy text-white overflow-hidden">
        {/* Decorative background pattern */}
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute -top-20 -left-20 w-80 h-80 bg-ktm-red/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -right-20 w-96 h-96 bg-ktm-red/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <motion.div 
            className="grid lg:grid-cols-12 gap-8 items-end mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="lg:col-span-8">
              <motion.div 
                className="inline-block mb-4 px-4 py-1.5 border border-ktm-red/40 bg-ktm-red/10"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <span className="text-ktm-red font-bold uppercase tracking-[0.2em] text-xs">
                  Crafted to Impress
                </span>
              </motion.div>
              <h2 className="font-chivo font-black text-4xl md:text-6xl leading-[0.95]">
                Quality<br />
                <span className="italic font-light text-ktm-red">Finishes</span>
                <span className="text-ktm-red">.</span>
              </h2>
            </div>
            <div className="lg:col-span-4">
              <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                Granite countertops. Custom cabinetry. Hardwood floors. Every detail engineered to last — and styled to stop you in your tracks.
              </p>
            </div>
          </motion.div>

          {/* Hero asymmetric showcase grid */}
          <div className="grid grid-cols-12 gap-3 md:gap-5">
            {/* Tall feature: The Mews */}
            <motion.div 
              className="col-span-12 md:col-span-5 row-span-2 relative overflow-hidden group"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="aspect-[3/4] md:aspect-auto md:h-full overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/d2s53f35_The%20Mews.jpeg"
                  alt="The Mews kitchen finish"
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ktm-navy via-transparent to-transparent opacity-60 group-hover:opacity-30 transition-opacity duration-500" />
              <div className="absolute top-5 left-5 flex items-center gap-2">
                <span className="w-8 h-[2px] bg-ktm-red" />
                <span className="text-ktm-red text-xs font-bold uppercase tracking-[0.25em]">Featured</span>
              </div>
              <div className="absolute bottom-6 left-6 right-6">
                <h3 className="font-chivo font-black text-3xl md:text-4xl text-white leading-tight">The Mews</h3>
                <p className="text-slate-200 text-sm mt-2 max-w-xs">Polished granite, two-tone shaker cabinetry, and brushed nickel hardware.</p>
              </div>
            </motion.div>

            {/* Gateway after */}
            <motion.div 
              className="col-span-6 md:col-span-4 relative overflow-hidden group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.15 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/zg8806v1_Gathway%20after.jpeg"
                  alt="Gateway after kitchen"
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ktm-navy/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <h3 className="font-chivo font-black text-xl text-white">Gateway · After</h3>
              </div>
            </motion.div>

            {/* Gateway */}
            <motion.div 
              className="col-span-6 md:col-span-3 relative overflow-hidden group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.3 }}
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_ktm-2026/artifacts/8v9wtm2y_Gateway.jpeg"
                  alt="Gateway open kitchen"
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-ktm-navy/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5">
                <h3 className="font-chivo font-black text-lg text-white">Gateway</h3>
              </div>
            </motion.div>

            {/* Interior finish */}
            <motion.div 
              className="col-span-6 md:col-span-4 relative overflow-hidden group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.45 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={IMAGES.interior} 
                  alt="Interior finishing" 
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
              </div>
            </motion.div>

            {/* Bathroom */}
            <motion.div 
              className="col-span-6 md:col-span-3 relative overflow-hidden group"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.6 }}
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src={IMAGES.bathroom} 
                  alt="Bathroom finishing" 
                  className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-110"
                />
              </div>
            </motion.div>
          </div>

          {/* Stats / Spec bar */}
          <motion.div 
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            {[
              { num: 'Granite', label: 'Premium Countertops' },
              { num: 'Shaker', label: 'Custom Cabinetry' },
              { num: 'Hardwood', label: 'Luxury Flooring' },
              { num: 'Spring', label: 'Designer Fixtures' },
            ].map((item) => (
              <div key={item.num} className="bg-ktm-navy px-6 py-8 text-center hover:bg-ktm-navy/70 transition-colors duration-300">
                <div className="font-chivo font-black text-2xl md:text-3xl text-ktm-red">{item.num}</div>
                <div className="text-slate-300 text-xs uppercase tracking-wider mt-2">{item.label}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-ktm-red">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-chivo font-black text-3xl md:text-4xl text-white mb-6">
              Ready to Start Your Project?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Contact us today for a free consultation and quote. 
              Our team is ready to help bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact?quote=true">
                <Button 
                  className="bg-white text-ktm-navy h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-slate-100 w-full sm:w-auto"
                  data-testid="services-quote-btn"
                >
                  Get Free Quote
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/gallery">
                <Button 
                  variant="outline"
                  className="bg-transparent text-white border-2 border-white h-14 px-8 rounded-none font-bold uppercase tracking-wider hover:bg-white/10 w-full sm:w-auto"
                  data-testid="services-gallery-btn"
                >
                  View Our Work
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
